/**
 * Pure helpers for the presentation mode: the slide deck (every dashboard, then every pinned
 * clone), stepping through it, and where the audience stands when the presenter pulls
 * everyone to the wall. Runs under Vitest.
 */
import { HALL, PRESENTATION } from '../world/plan.ts';
import {
  DASHBOARD_ARC_ORDER,
  DASHBOARD_TITLES,
  type DashboardId,
  type PinnedPanel,
  type Slide,
} from '@clinspace/shared';

export interface DeckEntry {
  slide: Slide;
  label: string;
}

/** The deck: the eleven dashboards at their overview, then every pin as it stands. */
export function buildDeck(pins: PinnedPanel[]): DeckEntry[] {
  const deck: DeckEntry[] = DASHBOARD_ARC_ORDER.map((dashboard) => ({
    slide: { kind: 'dashboard', dashboard, path: [], filters: {} },
    label: DASHBOARD_TITLES[dashboard],
  }));
  for (const pin of pins)
    deck.push({
      slide: { kind: 'pin', id: pin.id },
      label: `Pin: ${DASHBOARD_TITLES[pin.dashboard]}${pin.state.path.length ? ' (drilled)' : ''}`,
    });
  return deck;
}

export function sameSlide(a: Slide | null, b: Slide | null): boolean {
  if (!a || !b) return a === b;
  if (a.kind !== b.kind) return false;
  if (a.kind === 'pin' && b.kind === 'pin') return a.id === b.id;
  if (a.kind === 'dashboard' && b.kind === 'dashboard')
    return a.dashboard === b.dashboard && a.path.join('/') === b.path.join('/');
  return false;
}

/** Index of the current slide in the deck, or -1. */
export function deckIndex(deck: DeckEntry[], current: Slide | null): number {
  if (!current) return -1;
  return deck.findIndex((d) =>
    current.kind === 'dashboard' && d.slide.kind === 'dashboard'
      ? d.slide.dashboard === current.dashboard
      : sameSlide(d.slide, current),
  );
}

/** The next (+1) or previous (-1) slide; from nothing, the first or the last. */
export function stepDeck(deck: DeckEntry[], current: Slide | null, dir: 1 | -1): Slide | null {
  if (deck.length === 0) return null;
  const i = deckIndex(deck, current);
  const next = i < 0 ? (dir > 0 ? 0 : deck.length - 1) : (i + dir + deck.length) % deck.length;
  return deck[next]!.slide;
}

/** The dashboard a slide shows. */
export function slideDashboard(slide: Slide, pins: PinnedPanel[]): DashboardId | undefined {
  if (slide.kind === 'dashboard') return slide.dashboard;
  return pins.find((p) => p.id === slide.id)?.dashboard;
}

/**
 * Audience zone in front of the presentation display (on the entrance wall west of the door
 * since 2026-10-07, facing north): two rows of four spots 1.2 m apart, the first 1.8 m from
 * the wall, chosen from the session id so people spread out without talking to each other.
 */
export const AUDIENCE = {
  wallZ: HALL.front,
  /** Further back than for the old 5 m display: the 7.2 m one is read from 2.6 m and more. */
  front: 2.6,
  rows: 2,
  cols: 4,
  pitch: 1.2,
  xCentre: PRESENTATION.x,
};

export function audienceSpot(sessionId: string): { x: number; z: number } {
  let h = 0;
  for (let i = 0; i < sessionId.length; i++) h = (h * 31 + sessionId.charCodeAt(i)) >>> 0;
  const slot = h % (AUDIENCE.rows * AUDIENCE.cols);
  const row = Math.floor(slot / AUDIENCE.cols);
  const col = slot % AUDIENCE.cols;
  return {
    x: AUDIENCE.xCentre + (col - (AUDIENCE.cols - 1) / 2) * AUDIENCE.pitch,
    z: AUDIENCE.wallZ - AUDIENCE.front - row * AUDIENCE.pitch,
  };
}
